(function () {
  'use strict';

  const instances = [];
  const modelKeywords = ['wolverine', 'grizzly', 'timberwolf', 'owl'];

  class RecommendedAddons {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-addon-viewport]');
      this.cards = Array.from(root.querySelectorAll('[data-addon-card]'));
      this.status = root.querySelector('[data-addon-status]');
      this.mainVariantIds = (root.dataset.mainVariantIds || '').split(',').filter(Boolean);
      this.selected = new Map();

      this.prepareCards();
      this.bindCarousel();
      this.updateArrows();
    }

    prepareCards() {
      this.cards.forEach((card) => {
        const button = card.querySelector('[data-addon-toggle]');
        const matchedVariant = this.getMatchedVariant(card);

        if (!button || !matchedVariant || !matchedVariant.available) {
          button?.setAttribute('disabled', 'disabled');
          card.classList.add('is-unavailable');
          return;
        }

        card.dataset.variantId = matchedVariant.id;
        button.addEventListener('click', () => this.toggleCard(card, button));
      });
    }

    parseVariants(card) {
      return (card.dataset.addonVariants || '')
        .split('||')
        .filter(Boolean)
        .map((entry) => {
          const [id, title = '', available = 'false'] = entry.split('::');
          return { id, title: title.toLowerCase(), available: available === 'true' };
        });
    }

    getMatchedVariant(card) {
      const variants = this.parseVariants(card);
      const fallbackId = card.dataset.fallbackVariantId || '';
      const productTitle = (this.root.dataset.productTitle || '').toLowerCase();
      const model = modelKeywords.find((keyword) => productTitle.includes(keyword));
      const matched = model && variants.find((variant) => variant.title.includes(model) && variant.available);

      return matched || variants.find((variant) => variant.id === fallbackId && variant.available) || variants.find((variant) => variant.available);
    }

    toggleCard(card, button) {
      const variantId = card.dataset.variantId;
      const shouldSelect = !this.selected.has(variantId);

      if (shouldSelect) {
        this.selected.set(variantId, card.dataset.addonTitle || 'Add-on');
      } else {
        this.selected.delete(variantId);
      }

      card.classList.toggle('is-selected', shouldSelect);
      button.setAttribute('aria-pressed', shouldSelect ? 'true' : 'false');
      button.setAttribute('aria-label', `${shouldSelect ? 'Remove' : 'Add'} ${card.dataset.addonTitle || 'add-on'} ${shouldSelect ? 'from' : 'to'} selection`);
      this.updateStatus();
    }

    updateStatus() {
      if (!this.status) return;
      const count = this.selected.size;
      this.status.textContent = count ? `${count} add-on${count > 1 ? 's' : ''} selected. It will be added with the main product.` : '';
    }

    getAddonItems(quantity) {
      return Array.from(this.selected.keys()).map((id) => ({
        id: Number(id),
        quantity: Math.max(1, Number(quantity) || 1)
      }));
    }

    bindCarousel() {
      const previous = this.root.querySelector('[data-addon-prev]');
      const next = this.root.querySelector('[data-addon-next]');
      if (!this.viewport) return;

      previous?.addEventListener('click', () => this.scroll(-1));
      next?.addEventListener('click', () => this.scroll(1));
      this.viewport.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      window.addEventListener('resize', () => this.updateArrows(), { passive: true });
    }

    scroll(direction) {
      const card = this.cards[0];
      if (!card || !this.viewport) return;
      const gap = parseFloat(getComputedStyle(this.root).getPropertyValue('--addon-gap')) || 12;
      this.viewport.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    updateArrows() {
      if (!this.viewport) return;
      const previous = this.root.querySelector('[data-addon-prev]');
      const next = this.root.querySelector('[data-addon-next]');
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (previous) previous.disabled = this.viewport.scrollLeft <= 2;
      if (next) next.disabled = this.viewport.scrollLeft >= max - 2 || max <= 2;
    }
  }

  function bodyToItems(body) {
    if (!body) return null;

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (Array.isArray(parsed.items)) return { payload: parsed, items: parsed.items };
        if (parsed.id) return { payload: parsed, items: [{ id: parsed.id, quantity: parsed.quantity || 1 }] };
      } catch (error) {
        return paramsToItems(new URLSearchParams(body));
      }
    }

    if (body instanceof FormData || body instanceof URLSearchParams) {
      return paramsToItems(body);
    }

    return null;
  }

  function paramsToItems(params) {
    const id = params.get('id');
    if (!id) return null;

    const item = {
      id,
      quantity: Number(params.get('quantity')) || 1
    };

    const sellingPlan = params.get('selling_plan');
    if (sellingPlan) item.selling_plan = sellingPlan;

    const properties = {};
    for (const [key, value] of params.entries()) {
      const propertyMatch = key.match(/^properties\[(.+)\]$/);
      if (propertyMatch && value !== '') properties[propertyMatch[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;

    const payload = {};
    const sections = params.get('sections');
    const sectionsUrl = params.get('sections_url');
    if (sections) payload.sections = sections;
    if (sectionsUrl) payload.sections_url = sectionsUrl;

    return {
      payload,
      items: [item]
    };
  }

  function installCartInterceptor() {
    if (window.__recommendedAddonsFetchInstalled) return;
    window.__recommendedAddonsFetchInstalled = true;

    const originalFetch = window.fetch;
    window.fetch = function (input, init = {}, ...rest) {
      const url = typeof input === 'string' ? input : (input?.url || '');
      const method = (init.method || 'GET').toUpperCase();

      if (!url.includes('/cart/add') || method !== 'POST' || init.__recommendedAddonsHandled) {
        return originalFetch.call(this, input, init, ...rest);
      }

      const parsed = bodyToItems(init.body);
      if (!parsed) return originalFetch.call(this, input, init, ...rest);

      const mainItem = parsed.items.find((item) =>
        instances.some((instance) => instance.mainVariantIds.includes(String(item.id)) && instance.selected.size)
      );
      if (!mainItem) return originalFetch.call(this, input, init, ...rest);

      const instance = instances.find((candidate) =>
        candidate.mainVariantIds.includes(String(mainItem.id)) && candidate.selected.size
      );
      if (!instance) return originalFetch.call(this, input, init, ...rest);

      const extras = instance.getAddonItems(mainItem.quantity);
      const existingIds = new Set(parsed.items.map((item) => String(item.id)));
      const newItems = parsed.items.concat(extras.filter((item) => !existingIds.has(String(item.id))));
      const payload = Object.assign({}, parsed.payload, { items: newItems });
      delete payload.id;
      delete payload.quantity;

      return originalFetch.call(this, input, {
        ...init,
        __recommendedAddonsHandled: true,
        headers: { ...(init.headers || {}), 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      }, ...rest);
    };
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-recommended-addons]').forEach((root) => {
      if (root.dataset.addonsReady === 'true') return;
      root.dataset.addonsReady = 'true';
      instances.push(new RecommendedAddons(root));
    });
    installCartInterceptor();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
